import tw from 'twin.macro';
import { Clock, Mail, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { RerecordingStatus } from '@/libs/graphql';

interface RerecordingStatusBadgeProps {
  status: RerecordingStatus;
  size?: 'sm' | 'md';
  showIcon?: boolean;
}

export const RerecordingStatusBadge = ({ status, size = 'sm', showIcon = false}: RerecordingStatusBadgeProps) => {
  const { t } = useTranslation();

  const iconSize = size === 'sm' ? tw`h-3 w-3` : tw`h-4 w-4`;
  const padding = size === 'sm' ? tw`px-2 py-0.5` : tw`px-2.5 py-1`;
  const fontSize = size === 'sm' ? tw`text-xs` : tw`text-sm`;

  // Don't render for NONE status
  if (!status || status === RerecordingStatus.None) {
    return null;
  }

  switch (status) {
    case RerecordingStatus.NotificationScheduled:
      return (
        <span
          css={[
            tw`inline-flex items-center justify-center gap-1 rounded-full font-medium leading-none`,
            tw`bg-yellow-100 text-yellow-800`,
            padding,
            fontSize,
          ]}
        >
          {showIcon && <Clock css={[iconSize, tw`text-yellow-600`]} />}
          {t('rerecording.status.scheduled')}
        </span>
      );

    case RerecordingStatus.NotificationSent:
      return (
        <span
          css={[
            tw`inline-flex items-center justify-center gap-1 rounded-full font-medium leading-none`,
            tw`bg-blue-100 text-blue-800`,
            padding,
            fontSize,
          ]}
        >
          {showIcon && <Mail css={[iconSize, tw`text-blue-600`]} />}
          {t('rerecording.status.sent')}
        </span>
      );

    case RerecordingStatus.RerecordingInProgress:
      return (
        <span
          css={[
            tw`inline-flex items-center justify-center gap-1 rounded-full font-medium leading-none`,
            tw`bg-purple-100 text-purple-800`,
            padding,
            fontSize,
          ]}
        >
          {showIcon && <RefreshCw css={[iconSize, tw`text-purple-600`]} />}
          {t('rerecording.status.inProgress')}
        </span>
      );

    default:
      return null;
  }
};
